import React, { useState, useEffect } from "react";
import { getApiUrl } from "@/utils/api";
import { NAV_ICON_MAP, NAV_ICON_OPTIONS } from "@/utils/navIcons";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, Loader2, Download, Upload, Copy, Eye } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";

const API = getApiUrl();

export default function NavigationManager() {
  const [navItems, setNavItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // Fuels the "Pick a page" dropdown above the Path field — same
  // modular/preset/custom page list PageBuilder.jsx uses for its own
  // slug picker, so a nav item can point at any real page without the
  // admin having to know/type its exact path.
  const [allPages, setAllPages] = useState({ modular: [], preset: [], custom: [] });
  const [editingItem, setEditingItem] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  // Snapshot of formData captured the moment a dialog opens — used to detect
  // unsaved changes and warn the admin before discarding them on close.
  const [initialFormSnapshot, setInitialFormSnapshot] = useState(null);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  // Preview modal — opens when a navigation row title is clicked
  const [previewItem, setPreviewItem] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    path: "",
    order: 0,
    parent_id: null,
    environment: "both",
    enabled: true,
    show_in_top_nav: true,
    show_in_footer: false,
    icon: "",
    display_mode: "inherit",
    open_target: "same_tab",
    description: "",
    visibility: "always"
  });

  useEffect(() => {
    fetchNavigation();
    fetchAllPages();
  }, []);

  const fetchAllPages = async () => {
    try {
      const [compRes, navRes, customRes] = await Promise.all([
        axios.get(`${API}/admin/page-compositions`, { withCredentials: true }).catch(() => ({ data: { compositions: [] } })),
        axios.get(`${API}/navigation`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/custom-static-pages`, { withCredentials: true }).catch(() => ({ data: {} })),
      ]);
      setAllPages({
        modular: compRes.data.compositions || [],
        preset: (navRes.data || []).filter((p) => p.path && p.path !== "#"),
        custom: customRes.data.pages || [],
      });
    } catch (error) {
      console.error("Failed to fetch pages for the nav path picker:", error);
    }
  };

  const fetchNavigation = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/navigation`, {
        withCredentials: true
      });
      setNavItems(response.data || []);
    } catch (error) {
      console.error("Failed to fetch navigation:", error);
      toast.error("Failed to load navigation items");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    const fresh = {
      title: "",
      path: "",
      order: navItems.length,
      parent_id: null,
      environment: "both",
      enabled: true,
      show_in_top_nav: true,
      show_in_footer: false,
      icon: "",
      display_mode: "inherit",
      open_target: "same_tab",
      description: "",
      visibility: "always"
    };
    setFormData(fresh);
    setInitialFormSnapshot(JSON.stringify(fresh));
    setShowDialog(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    const fresh = {
      title: item.title,
      path: item.path,
      order: item.order,
      parent_id: item.parent_id || null,
      environment: item.environment,
      enabled: item.enabled,
      show_in_top_nav: item.show_in_top_nav !== false,
      show_in_footer: item.show_in_footer === true,
      icon: item.icon || "",
      display_mode: item.display_mode || "inherit",
      open_target: item.open_target || "same_tab",
      description: item.description || "",
      visibility: item.visibility || "always"
    };
    setFormData(fresh);
    setInitialFormSnapshot(JSON.stringify(fresh));
    setShowDialog(true);
  };

  // Wrap the dialog close — if the form has unsaved changes, ask the admin
  // to confirm before throwing those edits away.
  const requestCloseDialog = () => {
    if (initialFormSnapshot && initialFormSnapshot !== JSON.stringify(formData)) {
      const ok = confirm(
        "You have unsaved changes. Discard them and close this dialog?"
      );
      if (!ok) return;
    }
    setShowDialog(false);
    setInitialFormSnapshot(null);
  };

  const handleSave = async () => {
    try {
      if (!formData.title || !formData.path) {
        toast.error("Title and path are required");
        return;
      }

      if (editingItem) {
        // Update existing
        await axios.put(
          `${API}/admin/navigation/${editingItem.nav_id}`,
          formData,
          { withCredentials: true }
        );
        toast.success("Navigation item updated");
      } else {
        // Create new
        await axios.post(
          `${API}/admin/navigation`,
          formData,
          { withCredentials: true }
        );
        toast.success("Navigation item created");
      }

      setShowDialog(false);
      setInitialFormSnapshot(null);
      fetchNavigation();
    } catch (error) {
      console.error("Save error:", error);
      toast.error(error.response?.data?.detail || "Failed to save navigation item");
    }
  };

  const handleDelete = async (navId) => {
    const navItem = navItems.find(item => item.nav_id === navId);
    const itemName = navItem?.title || "this item";
    const hasChildren = navItems.some(item => item.parent_id === navId);

    let confirmMessage = `Remove "${itemName}" from the navigation?\n\nThis only removes the menu entry. The page itself (and any content on it) stays put — you can still reach it by its URL, and it will show up in Content Manager as usual.`;

    if (hasChildren) {
      confirmMessage += "\n\nChild nav items under it will also be removed from the menu.";
    }

    if (!confirm(confirmMessage)) {
      return;
    }

    try {
      // Delete ONLY the navigation item — backend cascades its children.
      // Pages and page content are intentionally left untouched: removing a
      // menu entry should never wipe the page it points at.
      await axios.delete(`${API}/admin/navigation/${navId}`, {
        withCredentials: true
      });

      toast.success(`Removed "${itemName}" from the navigation`);

      // Refresh navigation list to show updated items
      await fetchNavigation();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to remove navigation item");
    }
  };

  // Reorder ROOT-level items. `index` is the position inside the rootItems
  // list — NOT inside the full `navItems` (which also contains children).
  const reorderRoots = async (fromIndex, toIndex) => {
    const roots = navItems.filter((n) => !n.parent_id).sort((a, b) => (a.order || 0) - (b.order || 0));
    if (fromIndex < 0 || toIndex < 0 || fromIndex >= roots.length || toIndex >= roots.length) return;
    if (fromIndex === toIndex) return;
    const reordered = [...roots];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    const updates = reordered.map((item, idx) => ({ nav_id: item.nav_id, order: idx }));
    try {
      await axios.post(`${API}/admin/navigation/reorder`, updates, { withCredentials: true });
      // Re-fetch authoritative state from the server.
      await fetchNavigation();
      toast.success("Order updated");
    } catch (error) {
      console.error("Reorder error:", error);
      toast.error("Failed to reorder items");
      await fetchNavigation();
    }
  };

  const handleMoveUp = (_item, index) => reorderRoots(index, index - 1);
  const handleMoveDown = (_item, index) => reorderRoots(index, index + 1);

  const toggleEnabled = async (item) => {
    try {
      await axios.put(
        `${API}/admin/navigation/${item.nav_id}`,
        { enabled: !item.enabled },
        { withCredentials: true }
      );
      toast.success(`Navigation item ${!item.enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error("Toggle error:", error);
      toast.error("Failed to toggle navigation item");
    } finally {
      // Always re-sync with the server so the UI matches DB.
      fetchNavigation();
    }
  };

  // Toggle a boolean field directly on a nav item (e.g., show_in_top_nav, show_in_footer)
  const toggleField = async (item, field, value) => {
    // Optimistic update for snappy feedback.
    setNavItems((prev) =>
      prev.map((n) => (n.nav_id === item.nav_id ? { ...n, [field]: value } : n))
    );
    try {
      await axios.put(
        `${API}/admin/navigation/${item.nav_id}`,
        { [field]: value },
        { withCredentials: true }
      );
      toast.success("Updated");
    } catch (error) {
      console.error("toggleField error:", error);
      toast.error("Failed to update — refreshing");
    } finally {
      fetchNavigation();
    }
  };

  // Export navigation items as JSON file
  const handleExport = () => {
    try {
      const exportData = {
        version: "1.0",
        exported_at: new Date().toISOString(),
        items: navItems.map(item => ({
          nav_id: item.nav_id,
          title: item.title,
          path: item.path,
          order: item.order,
          parent_id: item.parent_id || null,
          environment: item.environment,
          enabled: item.enabled,
          icon: item.icon || "",
          description: item.description || ""
        }))
      };

      const jsonString = JSON.stringify(exportData, null, 2);

      // Try to download
      try {
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `navigation-export-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        toast.success(`Exported ${navItems.length} navigation items`);
      } catch (downloadError) {
        // Fallback: copy to clipboard
        navigator.clipboard.writeText(jsonString).then(() => {
          toast.success("Export data copied to clipboard! Save it as a .json file");
        }).catch(() => {
          toast.error("Download failed. Please try again or use 'Copy Export Data' button");
        });
      }
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export navigation items");
    }
  };

  // Copy export data to clipboard
  const handleCopyExport = () => {
    try {
      const exportData = {
        version: "1.0",
        exported_at: new Date().toISOString(),
        items: navItems.map(item => ({
          nav_id: item.nav_id,
          title: item.title,
          path: item.path,
          order: item.order,
          parent_id: item.parent_id || null,
          environment: item.environment,
          enabled: item.enabled,
          icon: item.icon || "",
          description: item.description || ""
        }))
      };

      const jsonString = JSON.stringify(exportData, null, 2);
      navigator.clipboard.writeText(jsonString).then(() => {
        toast.success("Export data copied to clipboard! Paste into a text editor and save as .json");
      }).catch((err) => {
        console.error("Copy error:", err);
        toast.error("Failed to copy to clipboard");
      });
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to prepare export data");
    }
  };

  // Import navigation items from JSON file
  const handleImport = async () => {
    if (!importFile) {
      toast.error("Please select a file to import");
      return;
    }

    setImporting(true);
    try {
      const text = await importFile.text();
      const data = JSON.parse(text);
      
      if (!data.items || !Array.isArray(data.items)) {
        throw new Error("Invalid import file format");
      }

      // Call backend API to import items
      await axios.post(
        `${API}/admin/navigation/import`,
        { items: data.items },
        { withCredentials: true }
      );

      toast.success(`Imported ${data.items.length} navigation items`);
      setShowImportDialog(false);
      setImportFile(null);
      fetchNavigation();
    } catch (error) {
      console.error("Import error:", error);
      toast.error(error.response?.data?.detail || "Failed to import navigation items");
    } finally {
      setImporting(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/json') {
      setImportFile(file);
    } else {
      toast.error("Please select a valid JSON file");
    }
  };

  // Get root items only for display
  const rootItems = navItems.filter(item => !item.parent_id);
  
  // Get all items that can be parents (for dropdown)
  const parentOptions = navItems.filter(item => !item.parent_id);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Navigation Manager</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Manage your website navigation menu. Create dropdown menus by setting a parent item.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={handleExport} disabled={navItems.length === 0}>
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
          <Button variant="outline" onClick={handleCopyExport} disabled={navItems.length === 0} title="Copy export data to clipboard">
            <Copy className="mr-2 h-4 w-4" />
            Copy Export Data
          </Button>
          <Button variant="outline" onClick={() => setShowImportDialog(true)}>
            <Upload className="mr-2 h-4 w-4" />
            Import
          </Button>
          <Button 
            onClick={() => {
              // Reset form for new page
              setFormData({
                title: "",
                path: "",
                order: navItems.length + 1,
                parent_id: null,
                environment: "both",
                enabled: true,
                show_in_top_nav: true,
                show_in_footer: false,
                icon: "",
                display_mode: "inherit",
                open_target: "same_tab",
                description: "",
                visibility: "always"
              });
              setEditingItem(null);
              setShowDialog(true);
            }}
            className="bg-green-600 hover:bg-green-700"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Page
          </Button>
          <Button onClick={handleAdd}>
            <Plus className="mr-2 h-4 w-4" />
            Add Navigation Item
          </Button>
        </div>
      </div>

      <div className="border rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">Order</TableHead>
              <TableHead>Title</TableHead>
              <TableHead>Path</TableHead>
              <TableHead>Parent</TableHead>
              <TableHead className="hidden md:table-cell">Environment</TableHead>
              <TableHead className="w-20">Top Nav</TableHead>
              <TableHead className="w-20">Footer</TableHead>
              <TableHead className="w-36">Visibility</TableHead>
              <TableHead className="w-20">Enabled</TableHead>
              <TableHead className="text-right w-24">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rootItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                  No navigation items yet. Click &quot;Add Navigation Item&quot; to get started.
                </TableCell>
              </TableRow>
            ) : (
              rootItems.map((item, index) => {
                const children = navItems.filter(child => child.parent_id === item.nav_id);
                return (
                  <React.Fragment key={item.nav_id}>
                    <TableRow data-testid={`nav-row-${item.nav_id}`}>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMoveUp(item, index)}
                            disabled={index === 0}
                          >
                            <ArrowUp className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMoveDown(item, index)}
                            disabled={index === rootItems.length - 1}
                          >
                            <ArrowDown className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">
                        <button
                          type="button"
                          onClick={() => setPreviewItem(item)}
                          className="text-left hover:text-primary hover:underline transition-colors"
                          title="Click to preview page"
                          data-testid={`nav-title-${item.nav_id}`}
                        >
                          {item.title}
                        </button>
                      </TableCell>
                      <TableCell>
                        <span className="font-mono text-xs text-muted-foreground">{item.path}</span>
                        {item.open_target && item.open_target !== "same_tab" && (
                          <span
                            className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-700"
                            data-testid={`nav-open-target-badge-${item.nav_id}`}
                          >
                            {item.open_target === "new_tab" ? "New tab" : "Modal"}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>-</TableCell>
                      <TableCell className="hidden md:table-cell">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          item.environment === 'prod' ? 'bg-green-100 text-green-800' :
                          item.environment === 'dev' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {item.environment}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Switch
                          data-testid={`nav-top-toggle-${item.nav_id}`}
                          checked={item.show_in_top_nav !== false}
                          onCheckedChange={(checked) => toggleField(item, "show_in_top_nav", checked)}
                        />
                      </TableCell>
                      <TableCell>
                        <Switch
                          data-testid={`nav-footer-toggle-${item.nav_id}`}
                          checked={item.show_in_footer === true}
                          onCheckedChange={(checked) => toggleField(item, "show_in_footer", checked)}
                        />
                      </TableCell>
                      <TableCell>
                        <Select
                          value={item.visibility || "always"}
                          onValueChange={(value) => toggleField(item, "visibility", value)}
                        >
                          <SelectTrigger className="h-8 text-xs" data-testid={`nav-visibility-select-${item.nav_id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="always">Always show</SelectItem>
                            <SelectItem value="signed_in">Signed-in only</SelectItem>
                            <SelectItem value="signed_out">Signed-out only</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={item.enabled}
                          onCheckedChange={() => toggleEnabled(item)}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1 flex-wrap">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(item);
                            }}
                            title="Edit navigation item"
                            data-testid={`nav-edit-btn-${item.nav_id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(item.nav_id);
                            }}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    {/* Render children with indentation */}
                    {children.map((child) => (
                      <TableRow key={child.nav_id} className="bg-muted/30">
                        <TableCell></TableCell>
                        <TableCell className="pl-8">
                          <button
                            type="button"
                            onClick={() => setPreviewItem(child)}
                            className="text-left hover:text-primary hover:underline transition-colors"
                            title="Click to preview page"
                          >
                            <span className="text-muted-foreground">↳</span> {child.title}
                          </button>
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {child.path}
                          {child.open_target && child.open_target !== "same_tab" && (
                            <span
                              className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-700"
                              data-testid={`nav-open-target-badge-${child.nav_id}`}
                            >
                              {child.open_target === "new_tab" ? "New tab" : "Modal"}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{item.title}</TableCell>
                        <TableCell className="hidden md:table-cell">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            child.environment === 'prod' ? 'bg-green-100 text-green-800' :
                            child.environment === 'dev' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {child.environment}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Switch
                            checked={child.show_in_top_nav !== false}
                            onCheckedChange={(checked) => toggleField(child, "show_in_top_nav", checked)}
                          />
                        </TableCell>
                        <TableCell>
                          <Switch
                            checked={child.show_in_footer === true}
                            onCheckedChange={(checked) => toggleField(child, "show_in_footer", checked)}
                          />
                        </TableCell>
                        <TableCell>
                          <Select
                            value={child.visibility || "always"}
                            onValueChange={(value) => toggleField(child, "visibility", value)}
                          >
                            <SelectTrigger className="h-8 text-xs" data-testid={`nav-visibility-select-${child.nav_id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="always">Always show</SelectItem>
                              <SelectItem value="signed_in">Signed-in only</SelectItem>
                              <SelectItem value="signed_out">Signed-out only</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Switch
                            checked={child.enabled}
                            onCheckedChange={() => toggleEnabled(child)}
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1 flex-wrap">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(child);
                              }}
                              title="Edit navigation item"
                              data-testid={`nav-edit-btn-${child.nav_id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(child.nav_id);
                              }}
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={(open) => { if (!open) requestCloseDialog(); else setShowDialog(true); }}>
        <DialogContent className="max-w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto mx-4">
          <DialogHeader>
            <DialogTitle>
              {editingItem ? "Edit Navigation Item" : "Add Navigation Item"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Free Valuation"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="path">Path *</Label>
                <select
                  className="w-full h-9 px-3 border border-input rounded-md text-sm bg-background"
                  value=""
                  onChange={(e) => {
                    if (!e.target.value) return;
                    setFormData((prev) => ({ ...prev, path: e.target.value }));
                  }}
                  data-testid="nav-path-page-picker"
                >
                  <option value="">Pick a page to fill the path below…</option>
                  {allPages.modular?.length > 0 && (
                    <optgroup label="Modular Pages">
                      {allPages.modular.map((p) => (
                        <option key={p.composition_id} value={p.page_slug ? `/${p.page_slug}` : "/"}>
                          {p.page_title} ({p.page_slug ? `/${p.page_slug}` : "/"})
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {allPages.preset?.length > 0 && (
                    <optgroup label="Preset Pages">
                      {allPages.preset.map((p) => (
                        <option key={p.nav_id} value={p.path}>
                          {p.title} ({p.path})
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {allPages.custom?.length > 0 && (
                    <optgroup label="Custom Pages">
                      {allPages.custom.map((p) => (
                        <option key={p.page_id} value={`/${p.slug}`}>
                          {p.title} (/{p.slug})
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
                <Input
                  id="path"
                  value={formData.path}
                  onChange={(e) => setFormData({ ...formData, path: e.target.value })}
                  placeholder="e.g., /valuation, or an external https:// link"
                  data-testid="nav-path-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="parent">Parent (for dropdown menu)</Label>
                <Select
                  value={formData.parent_id || "none"}
                  onValueChange={(value) => setFormData({ ...formData, parent_id: value === "none" ? null : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="None (root level)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (root level)</SelectItem>
                    {parentOptions.map((item) => (
                      <SelectItem key={item.nav_id} value={item.nav_id}>
                        {item.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="environment">Environment</Label>
                <Select
                  value={formData.environment}
                  onValueChange={(value) => setFormData({ ...formData, environment: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="both">Both (Dev & Prod)</SelectItem>
                    <SelectItem value="dev">Development Only</SelectItem>
                    <SelectItem value="prod">Production Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="order">Order</Label>
                <Input
                  id="order"
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="icon">Icon</Label>
                <div className="flex items-center gap-2">
                  <select
                    id="icon"
                    value={formData.icon || ""}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                    className="flex-1 h-10 px-3 border border-input bg-background rounded text-sm"
                    data-testid="nav-item-icon-select"
                  >
                    {NAV_ICON_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  {formData.icon && NAV_ICON_MAP[formData.icon] && (() => {
                    const Preview = NAV_ICON_MAP[formData.icon];
                    return (
                      <div
                        className="h-10 w-10 flex items-center justify-center border border-input rounded bg-muted/40 shrink-0"
                        data-testid="nav-item-icon-preview"
                        title={formData.icon}
                      >
                        <Preview className="h-5 w-5" />
                      </div>
                    );
                  })()}
                </div>
                <p className="text-xs text-muted-foreground">
                  Pick a Lucide icon, or leave on <em>Auto</em> to pick one based on the item&apos;s title/path.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Display (this item)</Label>
              <div className="inline-flex border border-border rounded overflow-hidden text-sm" role="radiogroup" data-testid="nav-item-display-mode">
                {[
                  { v: "inherit", label: "Inherit from module" },
                  { v: "both", label: "Icon + text" },
                  { v: "text", label: "Text only" },
                  { v: "icon", label: "Icon only" },
                ].map((opt) => {
                  const active = (formData.display_mode || "inherit") === opt.v;
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => setFormData({ ...formData, display_mode: opt.v })}
                      className={`px-3 py-1.5 font-medium transition-colors ${active ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
                      data-testid={`nav-item-display-mode-${opt.v}`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground">
                Overrides the Navigation module setting for this item only. Choose <em>Inherit</em> to follow the module default.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Open in</Label>
              <div className="inline-flex border border-border rounded overflow-hidden text-sm" role="radiogroup" data-testid="nav-item-open-target">
                {[
                  { v: "same_tab", label: "Same tab" },
                  { v: "new_tab", label: "New tab" },
                  { v: "modal", label: "Modal" },
                ].map((opt) => {
                  const active = (formData.open_target || "same_tab") === opt.v;
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => setFormData({ ...formData, open_target: opt.v })}
                      className={`px-3 py-1.5 font-medium transition-colors ${active ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
                      data-testid={`nav-item-open-target-${opt.v}`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground">
                How clicking this link behaves on the live site — normal navigation, a new browser tab, or an in-page modal overlay.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description (optional)</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this page"
                rows={2}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="enabled"
                checked={formData.enabled}
                onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked })}
              />
              <Label htmlFor="enabled">Enabled</Label>
            </div>

            <div className="border-t pt-4 space-y-3">
              <p className="text-sm font-medium">Where should this link appear?</p>
              <div className="flex items-center space-x-2">
                <Switch
                  id="show_in_top_nav"
                  data-testid="form-show-in-top-nav"
                  checked={formData.show_in_top_nav}
                  onCheckedChange={(checked) => setFormData({ ...formData, show_in_top_nav: checked })}
                />
                <Label htmlFor="show_in_top_nav">Show in top navigation</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="show_in_footer"
                  data-testid="form-show-in-footer"
                  checked={formData.show_in_footer}
                  onCheckedChange={(checked) => setFormData({ ...formData, show_in_footer: checked })}
                />
                <Label htmlFor="show_in_footer">Show in footer</Label>
              </div>
              <p className="text-xs text-muted-foreground">
                A link can appear in either or both locations.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={requestCloseDialog}>
              Cancel
            </Button>
            <Button onClick={handleSave} data-testid="nav-form-save-btn">
              {editingItem ? "Save Changes" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Import Navigation Items</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select JSON File</Label>
              <Input
                type="file"
                accept=".json"
                onChange={handleFileSelect}
                className="cursor-pointer"
              />
              {importFile && (
                <p className="text-sm text-muted-foreground">
                  Selected: {importFile.name}
                </p>
              )}
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded p-3">
              <p className="text-sm text-amber-800">
                <strong>⚠️ Warning:</strong> Importing will replace all existing navigation items with the items from the file.
              </p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded p-3">
              <p className="text-sm text-blue-800">
                <strong>💡 How to migrate:</strong>
              </p>
              <ol className="text-sm text-blue-800 mt-2 ml-4 list-decimal space-y-1">
                <li>Click &quot;Export&quot; in preview/development environment</li>
                <li>Download the JSON file</li>
                <li>Open production admin dashboard</li>
                <li>Click &quot;Import&quot; and upload the JSON file</li>
              </ol>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowImportDialog(false);
                setImportFile(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleImport}
              disabled={!importFile || importing}
            >
              {importing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Preview Modal — opened by clicking a page title in the table */}
      <Dialog open={!!previewItem} onOpenChange={(open) => !open && setPreviewItem(null)}>
        <DialogContent className="sm:max-w-6xl w-[95vw] h-[85vh] p-0 flex flex-col">
          <DialogHeader className="px-6 py-4 border-b">
            <DialogTitle className="flex items-center justify-between gap-4">
              <span data-testid="preview-modal-title">Preview — {previewItem?.title}</span>
              {previewItem?.path && (
                <a
                  href={previewItem.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:underline inline-flex items-center gap-1 font-normal"
                >
                  <Eye className="h-4 w-4" />
                  Open in new tab
                </a>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 bg-muted/30 overflow-hidden">
            {previewItem?.path && previewItem.path !== "#" && !previewItem.path.startsWith("http") ? (
              <iframe
                key={previewItem.nav_id}
                src={previewItem.path}
                title={`Preview of ${previewItem.title}`}
                className="w-full h-full border-0"
                data-testid="preview-modal-iframe"
              />
            ) : previewItem?.path?.startsWith("http") ? (
              <iframe
                key={previewItem.nav_id}
                src={previewItem.path}
                title={`Preview of ${previewItem.title}`}
                className="w-full h-full border-0"
              />
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                This navigation item has no previewable path.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
